import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import moment from "moment";
import firebase from "firebase/compat/app";

import AuditTable from "./AuditTable";
import { ACTION_OPTIONS, CATEGORIES, FIELDS, actionLabel, fieldLabel } from "./labels";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const PAGE_SIZE = 50;
// Base active : 24 mois ; au-delà (jusqu'à 5 ans pour le métier), archive sur demande
const ACTIVE_MONTHS = 24;

// Filtre servi par Firestore (un seul, le plus sélectif) ; les autres sont appliqués à
// l'affichage. Chaque filtre servi a son index composite (firestore.indexes.json).
const SERVER_FILTERS = [
  { key: "op", field: "source.op" },
  { key: "messageId", field: "target.message_id" },
  { key: "companyId", field: "target.company_id" },
  { key: "actor", field: "actor.real_uid" },
  { key: "field", field: "fields", op: "array-contains" },
  { key: "action", field: "action" },
  { key: "unattributed", field: "source.attribution", value: () => "none" },
  { key: "category", field: "category" },
];

function matches(entry, filters) {
  if (filters.op && (entry.source || {}).op !== filters.op) return false;
  if (filters.messageId && (entry.target || {}).message_id !== filters.messageId) return false;
  if (filters.companyId && (entry.target || {}).company_id !== filters.companyId) return false;
  if (filters.actor && (entry.actor || {}).real_uid !== filters.actor) return false;
  if (filters.field && !(entry.fields || []).includes(filters.field)) return false;
  if (filters.action && entry.action !== filters.action) return false;
  if (filters.unattributed && (entry.source || {}).attribution !== "none") return false;
  if (filters.category && entry.category !== filters.category) return false;
  return true;
}

export default function Journal() {
  const db = firebase.firestore();
  const [form] = Form.useForm();
  const [filters, setFilters] = useState({});
  const [entries, setEntries] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [staff, setStaff] = useState([]);
  const [verifyDay, setVerifyDay] = useState(moment().subtract(1, "day"));
  const [verifying, setVerifying] = useState(false);
  const [verification, setVerification] = useState(null);

  useEffect(() => {
    db.collection("companies")
      .get()
      .then((snap) =>
        setCompanies(
          snap.docs
            .map((doc) => ({ id: doc.id, name: doc.data().name || doc.id }))
            .sort((a, b) => a.name.localeCompare(b.name))
        )
      )
      .catch(() => setCompanies([]));
    db.collection("users")
      .get()
      .then((snap) =>
        setStaff(
          snap.docs
            .map((doc) => ({ uid: doc.id, ...doc.data() }))
            .map((u) => ({
              uid: u.uid,
              label: `${u.email || u.uid}${
                u.isAdmin ? " (admin)" : u.isOperator ? " (opérateur)" : ""
              }`,
              staff: !!(u.isAdmin || u.isOperator),
            }))
            .sort((a, b) => Number(b.staff) - Number(a.staff) || a.label.localeCompare(b.label))
        )
      )
      .catch(() => setStaff([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const companyNames = useMemo(
    () => Object.fromEntries(companies.map((company) => [company.id, company.name])),
    [companies]
  );

  const load = useCallback(
    async (activeFilters, after) => {
      setLoading(true);
      try {
        let query = db.collection("audit_logs");
        const serverFilter = SERVER_FILTERS.find((f) => activeFilters[f.key]);
        if (serverFilter) {
          const value = serverFilter.value ? serverFilter.value() : activeFilters[serverFilter.key];
          query = query.where(serverFilter.field, serverFilter.op || "==", value);
        }
        const [from, to] = activeFilters.period || [];
        const since = from
          ? from.clone().startOf("day")
          : activeFilters.archives
          ? null
          : moment().subtract(ACTIVE_MONTHS, "months");
        if (since)
          query = query.where("at", ">=", firebase.firestore.Timestamp.fromDate(since.toDate()));
        if (to) {
          query = query.where(
            "at",
            "<=",
            firebase.firestore.Timestamp.fromDate(to.clone().endOf("day").toDate())
          );
        }
        query = query.orderBy("at", "desc").limit(PAGE_SIZE);
        if (after) query = query.startAfter(after);

        const snap = await query.get();
        const page = snap.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((entry) => matches(entry, activeFilters));
        setEntries((previous) => (after ? [...previous, ...page] : page));
        setCursor(snap.docs[snap.docs.length - 1] || null);
        setHasMore(snap.docs.length === PAGE_SIZE);
      } catch (e) {
        console.error(e);
        message.error("Lecture du journal impossible");
      } finally {
        setLoading(false);
      }
    },
    [db]
  );

  useEffect(() => {
    load(filters, null);
  }, [filters, load]);

  function applyFilters(values) {
    const next = {};
    Object.entries(values).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "" && value !== false) {
        next[key] = typeof value === "string" ? value.trim() : value;
      }
    });
    setFilters(next);
  }

  function showOperation(op) {
    form.resetFields();
    form.setFieldsValue({ op, archives: true });
    setFilters({ op, archives: true });
  }

  async function verify() {
    if (!verifyDay) return;
    setVerifying(true);
    setVerification(null);
    try {
      const result = await firebase
        .app()
        .functions("europe-west1")
        .httpsCallable("verifyAuditSeal", { timeout: 300000 })({
        day: verifyDay.format("YYYY-MM-DD"),
      });
      setVerification(result.data);
    } catch (e) {
      message.error(e.message || "Vérification impossible");
    } finally {
      setVerifying(false);
    }
  }

  return (
    <>
      <h1>Journal d'audit</h1>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Traçabilité des actions (clients, opérateurs, admins, système)"
        description={
          "Les noms, adresses, e-mails et contenus n'y figurent pas : seuls les champs modifiés sont " +
          "indiqués. Conservation : métier 5 ans (24 mois affichés par défaut), accès 24 mois, " +
          "technique 12 mois."
        }
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical" onFinish={applyFilters}>
          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item name="period" label="Période">
                <RangePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="category" label="Catégorie">
                <Select allowClear placeholder="Toutes">
                  {Object.entries(CATEGORIES).map(([key, c]) => (
                    <Select.Option key={key} value={key}>
                      {c.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="action" label="Action">
                <Select allowClear showSearch optionFilterProp="children" placeholder="Toutes">
                  {ACTION_OPTIONS.map((action) => (
                    <Select.Option key={action} value={action}>
                      {actionLabel(action)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="field" label="Champ modifié">
                <Select allowClear showSearch optionFilterProp="children" placeholder="Tous">
                  {Object.keys(FIELDS).map((field) => (
                    <Select.Option key={field} value={field}>
                      {fieldLabel(field)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item name="companyId" label="Compte">
                <Select allowClear showSearch optionFilterProp="children" placeholder="Tous">
                  {companies.map((company) => (
                    <Select.Option key={company.id} value={company.id}>
                      {company.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item name="actor" label="Auteur (personne réelle)">
                <Select allowClear showSearch optionFilterProp="children" placeholder="Tous">
                  {staff.map((u) => (
                    <Select.Option key={u.uid} value={u.uid}>
                      {u.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="messageId" label="N° d'envoi (identifiant exact)">
                <Input allowClear placeholder="Copié depuis la fiche envoi" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="op" label="Opération">
                <Input allowClear placeholder="Identifiant d'opération" />
              </Form.Item>
            </Col>
            <Col xs={24} md={16}>
              <Space style={{ marginTop: 30 }} wrap>
                <Form.Item name="unattributed" valuePropName="checked" noStyle>
                  <Checkbox>Non attribuées uniquement</Checkbox>
                </Form.Item>
                <Form.Item name="archives" valuePropName="checked" noStyle>
                  <Checkbox>Inclure les archives (plus de 24 mois)</Checkbox>
                </Form.Item>
                <Button type="primary" htmlType="submit">
                  Filtrer
                </Button>
                <Button
                  onClick={() => {
                    form.resetFields();
                    setFilters({});
                  }}
                >
                  Réinitialiser
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      <AuditTable
        entries={entries}
        loading={loading}
        hasMore={hasMore}
        onLoadMore={() => load(filters, cursor)}
        companyNames={companyNames}
        onShowOperation={showOperation}
      />

      <Card size="small" title="Intégrité" style={{ marginTop: 24 }}>
        <Space wrap>
          <Text>Vérifier le scellé du</Text>
          <DatePicker
            value={verifyDay}
            onChange={setVerifyDay}
            format="DD/MM/YYYY"
            allowClear={false}
          />
          <Button onClick={verify} loading={verifying}>
            Vérifier
          </Button>
        </Space>
        {verification && (
          <div style={{ marginTop: 12 }}>
            {verification.results.map((r) => (
              <div key={r.category} style={{ marginBottom: 4 }}>
                <Tag color={r.ok ? "green" : "red"}>{r.ok ? "Intègre" : "Anomalie"}</Tag>
                {(CATEGORIES[r.category] || {}).label || r.category} :{" "}
                {r.reason
                  ? r.reason
                  : `${r.count_now} entrée(s) (${r.count_sealed} au scellé)` +
                    (r.altered_entries && r.altered_entries.length
                      ? ` — entrées modifiées : ${r.altered_entries.join(", ")}`
                      : "") +
                    (r.chain_intact === false ? " — chaînage rompu" : "") +
                    (r.seal_intact === false ? " — scellé altéré" : "")}
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}
